import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, delay, of, tap, throwError } from 'rxjs';

import { ApiError } from '../../models/api-error';
import { AppSessionService } from '../app-session/app-session.service';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';

type HttpMethods = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
type Services = 'auth' | 'systemusers' | 'profiles' | 'brands' | 'documenttypes' | 'assignments' | 'reports' | 'azure' | 'noticeconfidentiality' | 'settings' | 'folders' | 'documents' | 'directory' | 'explorer' | 'favorites';

const BASE_URL = `${environment.service.url}/api`;

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private readonly _http: HttpClient = inject(HttpClient);

  private _requestInProgress: boolean = false;

  /**
   * Request API in progress indicator.
   */
  public get requestInProgress(): boolean {
    return this._requestInProgress;
  }

  /**
   * Event to handle send requests to api.
   * @param method Http method.
   * @param endpoint Endpoint of api.
   * @param partialHeaders Request's headers.
   * @param params Http params.
   * @param body Body request.
   * @param responseType Response type.
   * @returns Observable with api response.
   */
  private send$(method: HttpMethods, endpoint: string, partialHeaders?: { [name: string]: string | number | (string | number)[]; }, params?: HttpParams, body?: any, responseType: 'json' | 'text' | 'blob' = 'json'): Observable<any> {
    try {

      // Configure url, params and body
      const url = `${BASE_URL}/${endpoint}`;
      !params && (params = new HttpParams());
      !body && (body = {});

      // Add access token to request if exists
      let headers = new HttpHeaders(partialHeaders);

      let observable$: Observable<any>;
      let options: Object = {};

      this._requestInProgress = true;

      // Send request
      switch (method) {
        case 'GET':
          options = { withCredentials: true, params, headers, responseType };
          observable$ = this._http.get<any>(url, options);
          break;
        case 'POST':
          options = { withCredentials: true, params, headers, responseType };
          observable$ = this._http.post<any>(url, body, options);
          break;
        case 'PUT':
          options = { withCredentials: true, params, headers, responseType };
          observable$ = this._http.put<any>(url, body, options);
          break;
        case 'PATCH':
          options = { withCredentials: true, params, headers, responseType };
          observable$ = this._http.patch<any>(url, body, options);
          break;
        case 'DELETE':
          options = { withCredentials: true, params, headers, body, responseType };
          observable$ = this._http.delete<any>(url, options);
          break;
        default:
          throw new Error('The request cannot be defined');
      }

      return observable$.pipe(
        delay(500),
        catchError((response: any) => {
          console.warn(response);

          const error: ApiError = {
            status: response.status,
            message: response.statusText,
            url: response.url,
            details: [],
            traceId: ''
          };

          if (error.url.startsWith(BASE_URL) && response.error) {

            if (responseType == 'text')
              response.error = JSON.parse(response.error);

            error.traceId = response.error.traceId;

            if (Array.isArray(response.error.errors)) {
              error.details = response.error.errors;
            } else {
              for (var key in response.error.errors) {
                error.details.push(`${response.error.errors[key]}\n`);
              }
            }
          }

          return throwError(() => error);
        }),
        tap({
          next: () => this._requestInProgress = false,
          error: (error) => this._requestInProgress = false
        })
      );
    } catch (error) {
      this._requestInProgress = false;
      throw error;
    }
  }

  /**
   * Observable to do a get request to api.
   * @param service Service.
   * @param endpoint Endpoint of api.
   * @param headers Request's headers.
   * @param params Http params.
   * @param body Body request.
   * @param responseType Response type.
   * @returns Observable with api response.
   */
  public get$(service: Services, endpoint: string, headers?: { [name: string]: string | number | (string | number)[]; }, params?: HttpParams, body?: any, responseType: 'json' | 'text' | 'blob' = 'json'): Observable<any> {
    return this.send$('GET', `${service}/${endpoint}`, headers, params, body, responseType);
  }

  /**
   * Observable to do a post request to api.
   * @param service Service.
   * @param endpoint Endpoint of api.
   * @param headers Request's headers.
   * @param params Http params.
   * @param body Body request
   * @param responseType Response type.
   * @returns Observable with api response.
   */
  public post$(service: Services, endpoint: string, headers?: { [name: string]: string | number | (string | number)[]; }, params?: HttpParams, body?: any, responseType: 'json' | 'text' | 'blob' = 'json'): Observable<any> {
    return this.send$('POST', `${service}/${endpoint}`, headers, params, body, responseType);
  }

  /**
   * Observable to do a put request to api.
   * @param service Service.
   * @param endpoint Endpoint of api.
   * @param headers Request's headers.
   * @param params Http params.
   * @param body Body request.
   * @param responseType Response type.
   * @returns Observable with api response.
   */
  public put$(service: Services, endpoint: string, headers?: { [name: string]: string | number | (string | number)[]; }, params?: HttpParams, body?: any, responseType: 'json' | 'text' | 'blob' = 'json'): Observable<any> {
    return this.send$('PUT', `${service}/${endpoint}`, headers, params, body, responseType);
  }

  /**
   * Observable to do a patch request to api.
   * @param service Service.
   * @param endpoint Endpoint of api.
   * @param headers Request's headers.
   * @param params Http params.
   * @param body Body request.
   * @param responseType Response type.
   * @returns Observable with api response.
   */
  public patch$(service: Services, endpoint: string, headers?: { [name: string]: string | number | (string | number)[]; }, params?: HttpParams, body?: any, responseType: 'json' | 'text' | 'blob' = 'json'): Observable<any> {
    headers = { 'Content-Type': 'application/json-patch+json', ...headers };
    return this.send$('PATCH', `${service}/${endpoint}`, headers, params, body, responseType);
  }

  /**
   * Observable to do a delete request to api.
   * @param service Service.
   * @param endpoint Endpoint of api.
   * @param headers Request's headers.
   * @param params Http params.
   * @param body Body request.
   * @param responseType Response type.
   * @returns Observable with api response.
   */
  public delete$(service: Services, endpoint: string, headers?: { [name: string]: string | number | (string | number)[]; }, params?: HttpParams, body?: any, responseType: 'json' | 'text' | 'blob' = 'json'): Observable<any> {
    return this.send$('DELETE', `${service}/${endpoint}`, headers, params, body, responseType);
  }
}